import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanActions = ({ 
  user, 
  plan, 
  onSavePlan, 
  onRegeneratePlan, 
  onExportPlan,
  isSaving = false,
  isRegenerating = false,
  className = ''
}) => {
  const [showExportMenu, setShowExportMenu] = useState(false);

  const handleSave = () => {
    if (!user) {
      // Redirect to authentication
      return;
    }
    onSavePlan?.(plan);
  };

  const handleExport = (format) => {
    onExportPlan?.(format);
    setShowExportMenu(false);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Primary Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Save Plan */}
        {user ? (
          <Button
            variant="default"
            size="default"
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
            className="flex-1"
          >
            {plan?.isSaved ? 'Update Plan' : 'Save Plan'}
          </Button>
        ) : (
          <Button
            variant="default"
            size="default"
            iconName="Save"
            iconPosition="left"
            asChild
            className="flex-1"
          >
            <Link to="/authentication?redirect=/ai-plan-generation">
              Sign In to Save
            </Link>
          </Button>
        )}

        {/* Start Exercises */}
        <Button
          variant="secondary"
          size="default"
          iconName="Play"
          iconPosition="left"
          asChild
          className="flex-1"
        >
          <Link to="/exercise-management">
            Start Exercises
          </Link>
        </Button>
      </div>
      {/* Secondary Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Regenerate Plan */}
        <Button
          variant="outline"
          size="default"
          loading={isRegenerating}
          iconName="RefreshCw"
          iconPosition="left"
          onClick={onRegeneratePlan}
          className="flex-1"
        >
          Regenerate Plan
        </Button>

        {/* Export Options - Premium Only */}
        {user?.isPremium ? (
          <div className="relative flex-1">
            <Button
              variant="outline"
              size="default"
              iconName="Download"
              iconPosition="left"
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="w-full"
            >
              Export Plan
            </Button>

            {showExportMenu && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg py-2 z-50">
                <button
                  onClick={() => handleExport('pdf')}
                  data-export-pdf
                  className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 flex items-center space-x-2"
                >
                  <Icon name="FileText" size={16} />
                  <span>Export as PDF</span>
                </button>
                <button
                  onClick={() => handleExport('csv')}
                  data-export-csv
                  className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 flex items-center space-x-2"
                >
                  <Icon name="Table" size={16} />
                  <span>Export as CSV</span>
                </button>
                <button
                  onClick={() => handleExport('json')}
                  data-export-json
                  className="w-full px-4 py-2 text-left text-sm text-popover-foreground hover:bg-muted transition-colors duration-200 flex items-center space-x-2"
                >
                  <Icon name="Code" size={16} />
                  <span>Export as JSON</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <Button
            variant="outline"
            size="default"
            iconName="Crown"
            iconPosition="left"
            asChild
            className="flex-1"
          >
            <Link to="/subscription-management">
              Upgrade for Export
            </Link>
          </Button>
        )}
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          iconName="BarChart3"
          iconPosition="left"
          asChild
        >
          <Link to="/progress-dashboard">
            View Progress
          </Link>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          iconName="Settings"
          iconPosition="left"
          asChild
          disabled={user?.role === 'free'}
          title={user?.role === 'free' ? 'Retakes are only available for Premium users' : 'Retake your assessment'}
        >
          <Link 
            to={user?.role === 'free' ? '/subscription-management' : '/career-assessment-setup'}
            state={user?.role === 'free' ? {
              message: 'Retakes are only available for Premium users. Upgrade to retake your assessment and unlock unlimited career guidance.',
              showUpgrade: true
            } : { isRetake: true }}
          >
            Retake Assessment
          </Link>
        </Button>

        {user?.isPremium && (
          <Button
            variant="ghost"
            size="sm"
            iconName="Share2"
            iconPosition="left"
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'My Career Development Plan',
                  text: 'Check out my personalized career plan from CareerLift',
                  url: window.location?.href
                });
              }
            }}
          >
            Share Plan
          </Button>
        )}
      </div>
      {/* Plan Info */}
      {plan && (
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Plan created:</span>
            <span className="font-medium text-foreground">
              {new Date(plan.createdAt)?.toLocaleDateString()}
            </span>
          </div>
          {plan?.lastUpdated && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Last updated:</span>
              <span className="font-medium text-foreground">
                {new Date(plan.lastUpdated)?.toLocaleDateString()}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Occupation:</span>
            <span className="font-medium text-foreground">
              {plan?.occupation} ({plan?.seniority})
            </span>
          </div>
        </div>
      )}
      {/* Click outside to close export menu */}
      {showExportMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowExportMenu(false)}
        />
      )}
    </div>
  );
};

export default PlanActions;